import React from 'react';
import { Icon } from './Icon.jsx';
import { AAConsent, loadHubSpot, loadCalendly } from './CookieBanner.jsx';
import { trackCTAClick, trackScheduleIntent, withUtm } from '../lib/tracking.js';

const HUBSPOT_PORTAL_ID = '6795575';
const HUBSPOT_FORM_ID = 'c603e83e-c654-4377-a666-4986126ea88f';
const HUBSPOT_REGION = 'na1';
const HUBSPOT_TARGET_ID = 'hs-guide-form';

const { useState: useState_m } = React;

const CALENDLY_URL = 'https://calendly.com/dominik-gross/infogesprach-app-academy-ws?hide_gdpr_banner=1';
const openCalMid = async (e) => {
  e.preventDefault();
  // Set consent BEFORE track*() so Meta pixel doesn't drop the events at its
  // consent gate. Click-wrap pattern: the booking-button click is treated as
  // implicit consent for Functional + Statistics + Marketing.
  const c = AAConsent.current();
  if (!c.functional || !c.statistics || !c.marketing) {
    AAConsent.set({ functional: true, statistics: true, marketing: true });
  }
  trackCTAClick({ label: 'Termin buchen', location: 'mid', destination: 'calendly' });
  trackScheduleIntent({ location: 'mid' });
  const url = withUtm(CALENDLY_URL);
  try {
    await loadCalendly();
    window.Calendly.initPopupWidget({ url });
  } catch {
    window.open(url, '_blank');
  }
};

// =================== CURRICULUM ===================
export function Curriculum() {
  const [active, setActive] = useState_m(0);
  const weeks = [
    {
      week: 'Woche 1',
      label: 'Idee, Konzept & Design',
      eyebrow: 'Modul 01 · Woche 1',
      title: 'Idee, Konzept und Design',
      desc: 'Du bringst deine App-Idee auf Papier und verwandelst sie in ein klares Konzept. Struktur, Navigation, Screens und der visuelle Look entstehen, bevor du eine Zeile Code brauchst. Du lernst, wie erfolgreiche Apps aufgebaut sind, und wendest das direkt auf dein eigenes Projekt an.',
      deliverables: ['MVP-Definition deiner App', 'User-Stories & Akzeptanzkriterien', 'Klickbare Screen-Designs (Google Stitch)', 'Visuelles Konzept & Branding']
    },
    {
      week: 'Woche 2',
      label: 'App-Entwicklung mit KI',
      eyebrow: 'Modul 02 · Woche 2',
      title: 'App-Entwicklung mit KI',
      desc: 'Hier wird es konkret. Du baust deine App mit KI als Werkzeug. Du beschreibst, was passieren soll. Die KI (Cursor mit Flutter, Gemini) schreibt den Code. Du siehst deine App Schritt für Schritt entstehen. Keine Programmierkenntnisse nötig.',
      deliverables: ['Erste lauffähige Version deiner App', 'GitHub als Sicherheitsanker eingerichtet', 'Effektives KI-Prompting für Features', 'Eigenes KI-Regelset für deine App']
    },
    {
      week: 'Woche 3',
      label: 'Daten & Backend',
      eyebrow: 'Modul 03 · Woche 3',
      title: 'Daten speichern & synchronisieren',
      desc: 'Deine App braucht ein Backend: Nutzerprofile, gespeicherte Inhalte, Einstellungen. Du lernst, wie ein Server im Hintergrund arbeitet und wie alles sicher gespeichert und auf jedem Gerät verfügbar wird. Klingt komplex, ist es mit KI aber nicht.',
      deliverables: ['Firestore-Datenbank DSGVO-konform', 'Login & Nutzerprofile funktional', 'Security Rules von Experten geprüft', 'Bilder & Dateien via Firebase Storage']
    },
    {
      week: 'Woche 4',
      label: 'Test auf echten Geräten',
      eyebrow: 'Modul 04 · Woche 4',
      title: 'Testen auf echten Geräten',
      desc: 'Was sich gut anfühlt und was nicht, erkennst du erst in der Hand, nicht am Bildschirm. Du installierst deine App via TestFlight und Internal Testing, testest alle Funktionen und optimierst die Nutzererfahrung, bis alles rund läuft.',
      deliverables: ['App live via TestFlight (iOS)', 'App live im Internal Testing (Android)', 'Code-Magic-Pipeline eingerichtet', 'Digitale Signatur & Keystore gesichert']
    },
    {
      week: 'Woche 5',
      label: 'Monetarisierung',
      eyebrow: 'Modul 05 · Woche 5',
      title: 'Mit deiner App Geld verdienen',
      desc: 'Werbung, Abos, Einmalkauf oder Freemium: Du lernst alle Geschäftsmodelle kennen und findest heraus, welches zu deiner App passt. Mit echten Zahlen und Beispielen aus der Praxis, damit du vom ersten Tag an Einnahmen generieren kannst.',
      deliverables: ['Passendes Geschäftsmodell ausgewählt', 'RevenueCat für In-App-Käufe', 'Abo-Logik (Free / Pro)', 'Admin-Tool für eigenes Content-Management']
    },
    {
      week: 'Woche 6',
      label: 'Launch im Store',
      eyebrow: 'Modul 06 · Woche 6',
      title: 'Launch: Veröffentlichung in den Stores',
      desc: 'Der finale Schritt: Deine App geht live. Du veröffentlichst sie im Apple App Store und im Google Play Store. Screenshots, Beschreibung, Keywords. Alles vorbereitet. Ab diesem Moment kann sie jeder auf der Welt herunterladen.',
      deliverables: ['Live im Apple App Store', 'Live bei Google Play', 'ASO: Suchmaschinen-optimierter Auftritt', 'Analytics & Feedback-Loop aktiv']
    },
  ];

  const w = weeks[active];

  return (
    <section className="section" id="lehrplan">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Dein 6-Wochen-Lehrplan</span>
          <h2 className="section-title">Sechs Module. Von der <span className="serif-italic">Idee</span> bis in den Store.</h2>
          <p className="lead">Jedes Modul baut auf das vorherige auf. Du arbeitest direkt an deiner eigenen App. Am Ende steht sie live im Store. Kein Zertifikat, kein Abschlusstest. Nur ein echtes Ergebnis.</p>
        </div>

        <div className="curriculum">
          <div className="week-tabs" role="tablist">
            {weeks.map((wk, i) => (
              <button key={i} className={`week-tab ${i === active ? 'active' : ''}`} onClick={() => setActive(i)} role="tab" aria-selected={i === active}>
                <span className="week-num">{String(i+1).padStart(2,'0')}</span>
                <span className="week-tab-name">{wk.label}</span>
                <span className="week-arrow"><Icon.ArrowRight size={14}/></span>
              </button>
            ))}
          </div>

          <div className="week-content" key={active}>
            <div className="week-content-eyebrow">{w.eyebrow}</div>
            <h3>{w.title}</h3>
            <p>{w.desc}</p>
            <ul className="week-points">
              {w.deliverables.map((d,i) => (
                <li key={i}><Icon.Check size={16}/> <span>{d}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================== COACH ===================
export function Coach() {
  return (
    <section className="section" id="coach" style={{paddingTop: 0}}>
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Wer dich begleitet</span>
          <h2 className="section-title">Du bekommst nicht <span className="serif-italic">einen</span> Coach. Du bekommst ein <span className="serif-italic">erprobtes Team</span>.</h2>
          <p className="lead">Hinter der App Academy steht die Incibit GmbH. Eine etablierte österreichische App-Agentur mit Senior Developern, die seit über 15 Jahren Apps für Unternehmen, Gründer und Visionäre umsetzen. Du profitierst nicht nur von einem einzelnen Trainer, sondern vom geballten Wissen eines kompletten App-Studios.</p>
        </div>
        <div className="coach-grid">
          <div className="coach-photo">
            <div className="coach-photo-frame">
              <div className="coach-photo-glow" aria-hidden="true"></div>
              <img src="/assets/dominik-gross.jpg" alt="Dominik Groß, Gründer der AI App Academy" width="640" height="800" loading="lazy" decoding="async" className="coach-photo-img"/>
              <div className="coach-photo-tag">
                <span className="coach-photo-tag-dot"></span>
                <span>Verfügbar für ein Infogespräch</span>
              </div>
            </div>
          </div>
          <div className="coach-content">
            <div className="coach-role">Gründer · App Academy · Incibit GmbH</div>
            <h3>Dominik Groß</h3>
            <p className="coach-bio">
              Dominik ist Gründer und Geschäftsführer der App-Agentur <a href="https://incibit.dev/" target="_blank" rel="noopener" className="coach-bio-link">Incibit</a>. Seit über 15 Jahren begleiten er und sein Team Unternehmen, Gründer und Visionäre dabei, aus Ideen erfolgreiche Apps zu machen. Gemeinsam haben sie mehr als 30 Apps umgesetzt, die von Millionen Menschen weltweit genutzt werden.
            </p>
            <p className="coach-bio">
              Seine Überzeugung: App-Entwicklung ist heute ohne Technikkenntnisse und ohne sechsstellige Budgets möglich. Mit der App Academy gibt er gemeinsam mit seinem Team aus Senior Developern dieses Wissen direkt aus 15 Jahren Praxis weiter.
            </p>
            <div className="coach-stats">
              <div>
                <div className="coach-stat-value">15+</div>
                <div className="coach-stat-label">Jahre Erfahrung</div>
              </div>
              <div>
                <div className="coach-stat-value">30+</div>
                <div className="coach-stat-label">Apps im Store</div>
              </div>
              <div>
                <div className="coach-stat-value">1 Mio.+</div>
                <div className="coach-stat-label">Downloads</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// =================== TESTIMONIALS ===================
export function Testimonials() {
  const items = [
    {
      stars: 5,
      quote: 'Unsere stetig wachsende Nutzerbasis freut sich über eine tolle App. Die Kommunikation war immer offen, freundlich und super schnell. Wer einen verlässlichen Partner mit hervorragender Ergebnisqualität sucht, ist bei Dominik und seinem Team bestens aufgehoben.',
      name: 'Milan Wolfs', role: 'Gründer, Gardify', photo: '/assets/milan-wolfs.jpg'
    },
    {
      stars: 5,
      quote: 'Unsere hohen Designansprüche konnten zu unserer vollsten Zufriedenheit umgesetzt werden. Die App sieht nicht nur großartig aus, sondern ist auch äußerst benutzerfreundlich.',
      name: 'Armin Kienreich', role: 'Gründer, Impulsschmiede', photo: '/assets/armin-kienreich.jpg'
    },
    {
      stars: 5,
      quote: 'Die Zusammenarbeit war durchgehend professionell, effizient und von einer ausgezeichneten Kommunikation geprägt. Ich habe nun eine App, die genau meinen Vorstellungen entspricht und großen Mehrwert für Nutzer bietet.',
      name: 'Daniel Bruck', role: 'Gründer, Uniprep', photo: '/assets/daniel-bruck.jpg'
    },
    {
      stars: 5,
      quote: 'Zusammen haben wir die App innerhalb kürzester Zeit konzipiert, entwickelt und erfolgreich gelaunched. Wir und unsere Kunden sind mit dem Resultat mehr als zufrieden.',
      name: 'Leonard Röser', role: 'Co-Founder, Schau aufs Land', photo: '/assets/leonard-roeser.jpg'
    },
    {
      stars: 5,
      quote: 'Wir sind begeistert von dem außergewöhnlich verlässlichen und überaus kompetenten Service, den wir erfahren haben. Vom Endergebnis sind wir wirklich beeindruckt, vor allem in der kurzen Zeit.',
      name: 'Denisa Ciul & Düzgün Cicek', role: 'Geschäftsführer, DDX Hair', photo: '/assets/ddx-hair.jpg'
    },
    {
      stars: 5,
      quote: 'Schon bevor die Entwicklung losging, hat das Team mit Kompetenz dafür gesorgt, dass alle Wünsche umgesetzt werden können. Das letztendliche Produkt hat unsere Erwartungen bei weitem übertroffen. Echte Software mit Handschlagqualität.',
      name: 'Alexander Wruss', role: 'CEO, Elevate Marketing', photo: '/assets/alexander-wruss.jpg'
    },
  ];
  return (
    <section className="section" id="stimmen" style={{paddingTop: 0}}>
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Stimmen aus der Community</span>
          <h2 className="section-title">Echte Menschen. Echte Apps. <span className="serif-italic">Echte Ergebnisse.</span></h2>
          <p className="lead">Über 30 Apps live im Store, Millionen Downloads, Ø 4,8 Sterne. Das sind die Menschen, die ihren Weg mit uns gegangen sind.</p>
        </div>

        <div className="testimonials-grid">
          {items.map((t,i) => (
            <div className="testimonial" key={i}>
              <div className="testimonial-quote-mark">"</div>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">{t.quote}</p>
              <div className="testimonial-author">
                {t.photo
                  ? <div className="testimonial-avatar testimonial-avatar-photo"><img src={t.photo} alt={`${t.name} – Teilnehmer-Testimonial`} width="64" height="64" loading="lazy" decoding="async"/></div>
                  : <div className="testimonial-avatar">{t.initial}</div>
                }
                <div>
                  <div className="testimonial-author-name">{t.name}</div>
                  <div className="testimonial-author-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== GUIDE CTA (Lead Magnet) ===================
export function GuideCTA() {
  const [hasConsent, setHasConsent] = React.useState(() => AAConsent.has('functional'));
  const [formStatus, setFormStatus] = React.useState('idle'); // idle | loading | ready | error
  const formRendered = React.useRef(false);

  React.useEffect(() => {
    const off = AAConsent.onChange((c) => setHasConsent(!!c.functional));
    return off;
  }, []);

  React.useEffect(() => {
    if (!hasConsent || formRendered.current) return;
    let cancelled = false;
    setFormStatus('loading');
    loadHubSpot()
      .then(() => {
        if (cancelled) return;
        if (window.hbspt && window.hbspt.forms) {
          window.hbspt.forms.create({
            portalId: HUBSPOT_PORTAL_ID,
            formId: HUBSPOT_FORM_ID,
            region: HUBSPOT_REGION,
            target: `#${HUBSPOT_TARGET_ID}`,
            // NOTE: Lead event is fired on the /guide thank-you page (GuidePage.jsx),
            // not here. That gives us a clean, race-free fire AFTER submission and before
            // the redirect to Google Drive. This callback stays as a hook for future use.
          });
          formRendered.current = true;
          setFormStatus('ready');
        } else {
          setFormStatus('error');
        }
      })
      .catch(() => { if (!cancelled) setFormStatus('error'); });
    return () => { cancelled = true; };
  }, [hasConsent]);

  return (
    <section className="section guide-section" id="guide" style={{paddingTop: 0}}>
      <div className="container">
        <div className="guide-card">
          <div className="guide-card-bg" aria-hidden="true"></div>
          <div className="guide-content">
            <div className="guide-mock" aria-hidden="true">
              <div className="guide-mock-paper">
                <div className="guide-mock-band"></div>
                <div className="guide-mock-eyebrow">App Academy Guide</div>
                <div className="guide-mock-title">In 6 Wochen<br/>zur eigenen App.</div>
                <div className="guide-mock-meta">
                  <span>6 Seiten</span>
                  <span className="guide-mock-dot"></span>
                  <span>Kostenlos</span>
                </div>
                <div className="guide-mock-lines">
                  <span></span><span></span><span style={{width:'70%'}}></span>
                </div>
                <div className="guide-mock-cta">Jetzt herunterladen</div>
              </div>
              <div className="guide-mock-paper guide-mock-paper-back" aria-hidden="true"></div>
            </div>

            <div className="guide-text">
              <span className="eyebrow">Kostenloser Download · App Academy Guide</span>
              <h2 className="section-title guide-title">Du willst es <span className="serif-italic">in Ruhe</span> durchlesen?</h2>
              <p className="lead guide-lead">Lade dir den kompletten App Academy Guide als PDF herunter: alle Module, der 6-Wochen-Lehrplan, was im Programm enthalten ist und wie der Ablauf konkret aussieht. Auf 6 Seiten zum Mitnehmen.</p>

              <ul className="guide-bullets">
                <li><Icon.Check size={14}/> Vollständiger 6-Wochen-Lehrplan</li>
                <li><Icon.Check size={14}/> Detaillierte Modul-Übersicht</li>
                <li><Icon.Check size={14}/> Was du am Ende kannst</li>
                <li><Icon.Check size={14}/> Ablauf & Investition transparent erklärt</li>
              </ul>

              <div className="guide-form-wrap">
                {!hasConsent ? (
                  <div className="guide-consent-gate">
                    <div className="guide-consent-gate-icon" aria-hidden="true"><Icon.Lock size={18}/></div>
                    <div className="guide-consent-gate-body">
                      <div className="guide-consent-gate-title">Formular benötigt deine Cookie-Zustimmung</div>
                      <p className="guide-consent-gate-text">
                        Wir laden das HubSpot-Formular nur mit deiner Zustimmung. Mit einem Klick aktivierst du die
                        nötigen Cookies und kannst den Guide direkt hier anfordern.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary guide-consent-btn"
                        onClick={() => AAConsent.set({ functional: true, statistics: true, marketing: true })}
                      >
                        <Icon.CheckCircle size={16}/>
                        <span>Cookies akzeptieren</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div id={HUBSPOT_TARGET_ID} className={`hs-guide-form-target hs-status-${formStatus}`} />
                    {formStatus === 'loading' && (
                      <div className="guide-form-loading"><span className="guide-form-spinner" aria-hidden="true"/> Formular wird geladen…</div>
                    )}
                    {formStatus === 'error' && (
                      <div className="guide-form-error">
                        Das Formular konnte nicht geladen werden. Bitte lade die Seite neu oder schreib uns direkt an{' '}
                        <a href="mailto:office@incibit.dev">office@incibit.dev</a>.
                      </div>
                    )}
                  </>
                )}
                <div className="guide-microcopy">
                  <Icon.Lock size={11}/>
                  <span>Kein Spam. Du kannst dich jederzeit wieder austragen. DSGVO-konform.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

